import type {
  CrmChatProviderKey,
  CrmLeadProviderChatAccount,
  CrmLeadProviderChatChannel,
  CrmLeadProviderChatDialog,
  CrmLeadProviderChatMeta,
} from '@/api/types/crm/crmLeadFunnels.types'
import { getWazzupChannelTypeLabel } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import {
  getLeadProviderChatAccounts,
  isWazzupLikeAccount,
} from './leadProviderChatAccounts'

export type CrmProviderChatMenuDialog = {
  key: string
  conversation_id: number
  label: string
  unread_count: number
  isAccessible?: boolean
}

export type CrmProviderChatMenuChannel = {
  key: string
  label: string
  /** Подпись типа канала (WhatsApp / Instagram / VK…), если есть name. */
  typeLabel?: string | null
  /** Сырой type канала (whatsapp / viber / tgapi…) для иконки. */
  channelType?: string | null
  unread_count: number
  isAccessible?: boolean
  /**
   * Leaf-канал: у лида на канал обычно один чат — клик сразу открывает диалог.
   * Если диалогов несколько — используем `dialogs`.
   */
  conversation_id?: number
  dialogs: CrmProviderChatMenuDialog[]
}

/**
 * Пункт меню провайдера.
 * - VK/TG/MAX: плоский leaf (`conversation_id`)
 * - Wazzup: аккаунт → канал (канал = leaf с conversation_id; без третьего «чат»)
 */
export type CrmProviderChatMenuAccount = {
  key: string
  integration_id: number
  label: string
  unread_count: number
  isAccessible?: boolean
  /** Плоский пункт (VK/TG/MAX или один канал Wazzup на верхнем уровне). */
  conversation_id?: number
  channels?: CrmProviderChatMenuChannel[]
}

const dialogLabel = (
  dialog: CrmLeadProviderChatDialog,
  fallbackPrefix: string,
): string => {
  const title = dialog.title?.trim()
  if (title) return title
  const recipient = String(dialog.recipient ?? '').trim()
  if (recipient && recipient !== '-') return recipient
  return `${fallbackPrefix} #${dialog.conversation_id}`
}

const channelLabel = (channel: CrmLeadProviderChatChannel): string => {
  const name = channel.name?.trim()
  if (name) return name
  const typeLabel = getWazzupChannelTypeLabel(String(channel.type ?? ''))
  if (typeLabel && typeLabel !== 'неизвестный') {
    return typeLabel
  }
  const type = channel.type?.trim()
  if (type) return type
  return `Канал ${channel.external_channel_id.slice(0, 8)}`
}

/** Badge типа рядом с именем; null если тип уже в label или неизвестен. */
const channelTypeBadgeLabel = (
  channel: CrmLeadProviderChatChannel,
  label: string,
): string | null => {
  const typeLabel = getWazzupChannelTypeLabel(String(channel.type ?? ''))
  if (!typeLabel || typeLabel === 'неизвестный') {
    return null
  }
  if (label.trim().toLowerCase() === typeLabel.toLowerCase()) {
    return null
  }
  return typeLabel
}

const accountLabel = (
  account: CrmLeadProviderChatAccount,
  provider: CrmChatProviderKey,
): string => {
  const name = account.name?.trim()
  if (name) return name
  if (provider === 'vkontakte') {
    return `Сообщество #${account.integration_id}`
  }
  if (provider === 'wazzup') {
    return `Аккаунт #${account.integration_id}`
  }
  return `Бот #${account.integration_id}`
}

const mapDialog = (
  dialog: CrmLeadProviderChatDialog,
  account: CrmLeadProviderChatAccount,
  channel: CrmLeadProviderChatChannel,
  isAccessible: boolean,
  fallbackPrefix: string,
): CrmProviderChatMenuDialog => ({
  key: [
    account.integration_id,
    channel.external_channel_id,
    dialog.conversation_id,
  ].join(':'),
  conversation_id: dialog.conversation_id,
  label: dialogLabel(dialog, fallbackPrefix),
  unread_count: Number(dialog.unread_count) || 0,
  isAccessible,
})

const mapWazzupChannel = (
  account: CrmLeadProviderChatAccount,
  channel: CrmLeadProviderChatChannel,
  isAccessible: boolean,
  dialogFallback: string,
): CrmProviderChatMenuChannel | null => {
  const dialogs = (channel.dialogs ?? [])
    .filter((dialog) => {
      const id = Number(dialog.conversation_id)
      return Number.isFinite(id) && id > 0
    })
    .map((dialog) =>
      mapDialog(dialog, account, channel, isAccessible, dialogFallback),
    )

  if (dialogs.length === 0) {
    return null
  }

  const unreadFromDialogs = dialogs.reduce(
    (sum, dialog) => sum + (Number(dialog.unread_count) || 0),
    0,
  )
  const unreadCount = Number(channel.unread_count) || unreadFromDialogs
  const label = channelLabel(channel)
  const typeLabel = channelTypeBadgeLabel(channel, label)
  const channelType = String(channel.type ?? '').trim() || null

  // Один чат на канал — канал сразу leaf (без третьего уровня «Выберите чат»).
  if (dialogs.length === 1) {
    return {
      key: `${account.integration_id}:${channel.external_channel_id}`,
      label,
      typeLabel,
      channelType,
      unread_count: unreadCount,
      isAccessible,
      conversation_id: dialogs[0].conversation_id,
      dialogs,
    }
  }

  return {
    key: `${account.integration_id}:${channel.external_channel_id}`,
    label,
    typeLabel,
    channelType,
    unread_count: unreadCount,
    isAccessible,
    dialogs,
  }
}

export type BuildLeadProviderChatMenuOptions = {
  /** Доступ по integration_id; нет ключа → считаем доступным. */
  accessByIntegrationId?: Record<number, boolean>
}

/**
 * Меню кнопки чата:
 * - VK/TG/MAX — плоский список диалогов
 * - Wazzup — аккаунт → канал (клик по каналу открывает чат; 1 чат на канал)
 */
export const buildLeadProviderChatMenuAccounts = (
  meta: CrmLeadProviderChatMeta | null | undefined,
  provider: CrmChatProviderKey,
  options?: BuildLeadProviderChatMenuOptions,
): CrmProviderChatMenuAccount[] => {
  const accessByIntegrationId = options?.accessByIntegrationId
  const dialogFallback = provider === 'wazzup' ? 'Чат' : 'Диалог'

  if (provider === 'wazzup') {
    const nested: CrmProviderChatMenuAccount[] = []

    getLeadProviderChatAccounts(meta).forEach((account) => {
      if (!isWazzupLikeAccount(account) && !account.channels?.length) {
        return
      }

      const integrationId = Number(account.integration_id)
      const isAccessible =
        accessByIntegrationId == null ||
        accessByIntegrationId[integrationId] !== false

      const channels = (account.channels ?? [])
        .map((channel) =>
          mapWazzupChannel(account, channel, isAccessible, dialogFallback),
        )
        .filter((channel): channel is CrmProviderChatMenuChannel => channel != null)

      if (channels.length === 0) {
        return
      }

      nested.push({
        key: String(account.integration_id),
        integration_id: integrationId,
        label: accountLabel(account, provider),
        unread_count: Number(account.unread_count) || 0,
        isAccessible,
        channels,
      })
    })

    return nested
  }

  // VK / TG / MAX — один уровень: имя аккаунта (сообщества/бота) → сразу диалог
  const flat: CrmProviderChatMenuAccount[] = []

  getLeadProviderChatAccounts(meta).forEach((account) => {
    const integrationId = Number(account.integration_id)
    const isAccessible =
      accessByIntegrationId == null ||
      accessByIntegrationId[integrationId] !== false

    const dialogs = (account.dialogs ?? []).filter((dialog) => {
      const id = Number(dialog.conversation_id)
      return Number.isFinite(id) && id > 0
    })

    dialogs.forEach((dialog) => {
      const label =
        accountLabel(account, provider) ||
        dialogLabel(dialog, dialogFallback)

      flat.push({
        key: `${account.integration_id}:${dialog.conversation_id}`,
        integration_id: integrationId,
        label,
        unread_count: Number(dialog.unread_count) || 0,
        isAccessible,
        conversation_id: dialog.conversation_id,
      })
    })
  })

  return flat
}

const collectAccessibleConversationIds = (
  accounts: CrmProviderChatMenuAccount[],
): number[] => {
  const ids: number[] = []

  for (const account of accounts) {
    if (account.isAccessible === false) {
      continue
    }

    if (account.channels) {
      for (const channel of account.channels) {
        if (channel.isAccessible === false) {
          continue
        }

        if (channel.conversation_id != null) {
          const id = Number(channel.conversation_id)
          if (Number.isFinite(id) && id > 0) {
            ids.push(id)
          }
          continue
        }

        for (const dialog of channel.dialogs) {
          if (dialog.isAccessible === false) {
            continue
          }
          const id = Number(dialog.conversation_id)
          if (Number.isFinite(id) && id > 0) {
            ids.push(id)
          }
        }
      }
      continue
    }

    const id = Number(account.conversation_id)
    if (Number.isFinite(id) && id > 0) {
      ids.push(id)
    }
  }

  return ids
}

export const countLeadProviderChatMenuDialogs = (
  accounts: CrmProviderChatMenuAccount[],
): number => collectAccessibleConversationIds(accounts).length

export const hasAccessibleLeadProviderChatMenuDialog = (
  accounts: CrmProviderChatMenuAccount[],
): boolean => collectAccessibleConversationIds(accounts).length > 0
